"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";
import { useLanguage } from "@/hooks/useLanguage";

export function PublicNavigation({
  className = "",
  isMobile = false,
  isFooter = false,
}) {
  const { t } = useLanguage();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});

  // Static navigation structure for public routes with i18n labels
  const navigation = [
    { name: t("navigation.home"), href: "/" },
    {
      name: t("navigation.about"),
      children: [
        { name: t("navigation.aboutLJPR"), href: "/about-ljpr" },
        { name: t("navigation.ourFounder"), href: "/our-inspiration" },
        {
          name: t("navigation.nationalPresident"),
          href: "/our-leaders/category/National%20Unit",
        },
        { name: t("navigation.ourLeaders"), href: "/our-leaders" },
      ],
    },
    {
      name: t("navigation.membership"),
      children: [
        {
          name: t("navigation.joinLJPR"),
          href: "https://membership.ljpr.in/?type=member",
        },
        {
          name: t("navigation.applyAsCandidate"),
          href: "https://membership.ljpr.in/?from=candidate",
        },
      ],
    },
    { name: t("navigation.programs"), href: "/event" },

    {
      name: t("navigation.mediaResources"),
      children: [
        { name: t("navigation.newsUpdates"), href: "/blog" },
        { name: t("navigation.mediaCoverage"), href: "/media-coverage" },
        { name: t("navigation.pressReleases"), href: "/press-release" },
        { name: t("navigation.photoGallery"), href: "/gallery" },
        { name: t("navigation.videoGallery"), href: "/video" },
      ],
    },
    { name: t("navigation.contactUs"), href: "/contact-us" },
  ];

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (openDropdown && buttonRefs.current[openDropdown]) {
        const rect = buttonRefs.current[openDropdown].getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    };

    updateDropdownPosition();

    const handleScroll = () => {
      updateDropdownPosition();
    };

    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        !event.target.closest(".public-navigation") &&
        !event.target.closest(".footer-navigation")
      ) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDropdown]);

  // Mobile layout
  if (isMobile) {
    return (
      <nav className={`flex flex-col space-y-2 sm:space-y-3 ${className}`}>
        {navigation.map((menu) => {
          if (menu.children && menu.children.length > 0) {
            return (
              <div key={menu.name} className="py-1 sm:py-2 px-2">
                <details className="group">
                  <summary className="text-white hover:text-destructive transition-all duration-300 cursor-pointer flex items-center justify-between font-poppins text-sm sm:text-base">
                    <span className="truncate language-transition flex items-center gap-1">
                      {menu.name}
                    </span>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 group-open:rotate-180 transition-transform flex-shrink-0 ml-2" />
                  </summary>
                  <div className="mt-2 ml-3 sm:ml-4 space-y-1 sm:space-y-2">
                    {menu.children.map((child) => (
                      <Link
                        key={child.name}
                        href={child.href}
                        className="flex text-white/80 hover:text-white py-1 sm:py-2 text-sm sm:text-base truncate items-center gap-1 hover:bg-white/5 rounded px-2 transition-all duration-200"
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                </details>
              </div>
            );
          } else {
            return (
              <Link
                key={menu.name}
                href={menu.href}
                className="text-white hover:text-destructive transition-all duration-300 py-2 sm:py-3 px-2 sm:px-3 rounded-md hover:bg-white/10 font-poppins text-sm sm:text-base truncate language-transition flex items-center gap-1"
              >
                {menu.name}
              </Link>
            );
          }
        })}
      </nav>
    );
  }

  // Footer layout with custom dropdown
  if (isFooter) {
    return (
      <div className="footer-navigation relative">
        <div
          className={`flex flex-wrap justify-center md:justify-start gap-2 sm:gap-4 ${className}`}
        >
          {navigation.map((menu) => {
            if (menu.children && menu.children.length > 0) {
              return (
                <div key={menu.name} className="relative">
                  <button
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === menu.name ? null : menu.name
                      )
                    }
                    className="hover:underline text-primary-foreground hover:text-destructive transition-colors whitespace-nowrap flex items-center gap-1"
                  >
                    {menu.name}
                    <ChevronDown
                      className={`h-3 w-3 ml-1 transition-transform ${
                        openDropdown === menu.name ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {openDropdown === menu.name && (
                    <div className="absolute bottom-full left-0 mb-2 min-w-[200px] bg-white rounded-sm shadow-lg border border-gray-200 overflow-hidden z-[9999]">
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                        <span className="text-xs font-medium text-gray-700">
                          {menu.name}
                        </span>
                      </div>
                      <div className="py-1">
                        {menu.children.map((child) => (
                          <Link
                            key={child.name}
                            href={child.href}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                            <span>{child.name}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            } else {
              return (
                <Link
                  key={menu.name}
                  href={menu.href}
                  className="hover:underline text-primary-foreground hover:text-destructive transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  {menu.name}
                </Link>
              );
            }
          })}
        </div>
      </div>
    );
  }

  // Desktop header layout with custom dropdown
  return (
    <>
      <div className="public-navigation relative">
        <div
          className={`flex items-center gap-0.5 sm:gap-1 md:gap-1.5 xl:gap-2 font-poppins flex-shrink-0 ${className}`}
        >
          {navigation.map((menu) => {
            if (menu.children && menu.children.length > 0) {
              return (
                <div key={menu.name} className="relative">
                  <button
                    ref={(el) => (buttonRefs.current[menu.name] = el)}
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === menu.name ? null : menu.name
                      )
                    }
                    className="text-white hover:text-destructive transition-all duration-300 px-1 sm:px-1 md:px-1.5 xl:px-2 py-0.5 text-[11px] sm:text-xs md:text-sm whitespace-nowrap flex items-center gap-1 hover:bg-white/10 rounded"
                  >
                    {menu.name}
                    <ChevronDown
                      className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ml-1 transition-transform ${
                        openDropdown === menu.name ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
              );
            } else {
              return (
                <Link
                  key={menu.name}
                  href={menu.href}
                  className="text-white hover:text-destructive transition-all duration-300 px-1 sm:px-1 md:px-1.5 xl:px-2 py-0.5 text-[11px] sm:text-xs md:text-sm whitespace-nowrap flex items-center gap-1 hover:bg-white/10 rounded"
                >
                  {menu.name}
                </Link>
              );
            }
          })}
        </div>
      </div>

      {/* Dropdown rendered outside - fixed positioning */}
      {openDropdown && (
        <div
          className="fixed min-w-[180px] sm:min-w-[200px] md:min-w-[220px] bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[99999]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {navigation
            .find((menu) => menu.name === openDropdown)
            ?.children?.map((child) => (
              <Link
                key={child.name}
                href={child.href}
                className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 hover:text-primary transition-colors w-full rounded"
              >
                <span>{child.name}</span>
              </Link>
            ))}
        </div>
      )}
    </>
  );
}

export default PublicNavigation;
